import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Grid,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InboxOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../../api/client';
import { usePermissions } from '../../../context/permissionsContext';
import { ESTADO_COLORS, ESTADO_HEX, ESTADO_LABELS, estadoOptions, sexoOptions } from './curriculumConstants';
import { inferSexoFromNombre, sexoDisplay } from './inferSexoFromNombre';
import { formatFechaCorta, parseFechaCalendario } from '../../../utils/fechaCalendario';

const { Title, Text } = Typography;
const { Dragger } = Upload;

const EstadoDot = ({ estado, size = 10 }) => (
  <span
    title={ESTADO_LABELS[estado] || estado}
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: ESTADO_HEX[estado] || '#d9d9d9',
      flexShrink: 0,
      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.08)',
    }}
  />
);

const renderEstadoOption = (option) => (
  <Space size={8} align="center">
    <EstadoDot estado={option.value} size={8} />
    <span>{option.label}</span>
  </Space>
);

const Curriculums = () => {
  const { hasPermission } = usePermissions();
  const screens = Grid.useBreakpoint();
  const isNarrow = screens.md === false;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);

  const [filtroNombre, setFiltroNombre] = useState('');
  const [filtroLocalidad, setFiltroLocalidad] = useState('');
  const [filtroEspecialidad, setFiltroEspecialidad] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(undefined);
  const [filtroSexo, setFiltroSexo] = useState(undefined);
  const [filtrosDebounced, setFiltrosDebounced] = useState({
    nombre: '',
    localidad: '',
    especialidad: '',
    estado: undefined,
    sexo: undefined,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [archivoFile, setArchivoFile] = useState(null);
  const [quitarArchivo, setQuitarArchivo] = useState(false);
  const [localidadOptions, setLocalidadOptions] = useState([]);
  const [form] = Form.useForm();
  const sexoManualRef = useRef(false);

  const loadLocalidadOptions = useCallback(async () => {
    try {
      const { data: res } = await api.get('/localidades/opciones');
      setLocalidadOptions(res.items || []);
    } catch {
      message.error('No se pudieron cargar las localidades');
    }
  }, []);

  useEffect(() => {
    loadLocalidadOptions();
  }, [loadLocalidadOptions]);

  useEffect(() => {
    const t = setTimeout(
      () =>
        setFiltrosDebounced({
          nombre: filtroNombre.trim(),
          localidad: filtroLocalidad.trim(),
          especialidad: filtroEspecialidad.trim(),
          estado: filtroEstado,
          sexo: filtroSexo,
        }),
      350
    );
    return () => clearTimeout(t);
  }, [filtroNombre, filtroLocalidad, filtroEspecialidad, filtroEstado, filtroSexo]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/curriculums', {
        params: {
          page,
          limit,
          nombre: filtrosDebounced.nombre || undefined,
          localidad: filtrosDebounced.localidad || undefined,
          especialidad: filtrosDebounced.especialidad || undefined,
          estado: filtrosDebounced.estado || undefined,
          sexo: filtrosDebounced.sexo || undefined,
        },
      });
      setData(res.items || []);
      setTotal(res.total ?? 0);
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al cargar currículums');
    } finally {
      setLoading(false);
    }
  }, [page, limit, filtrosDebounced]);

  useEffect(() => {
    load();
  }, [load]);

  const buildFormData = (values) => {
    const fd = new FormData();
    fd.append('fecha', values.fecha.format('YYYY-MM-DD'));
    fd.append('nombreApellido', values.nombreApellido.trim());
    fd.append('sexo', values.sexo);
    if (values.telefono?.trim()) fd.append('telefono', values.telefono.trim());
    fd.append('localidadId', values.localidadId);
    fd.append('email', values.email.trim());
    fd.append('especialidad', values.especialidad.trim());
    fd.append('observaciones', values.observaciones?.trim() || '');
    fd.append('estado', values.estado);
    if (archivoFile) fd.append('archivo', archivoFile);
    if (quitarArchivo) fd.append('quitarArchivo', 'true');
    return fd;
  };

  const openCreate = () => {
    setEditing(null);
    setArchivoFile(null);
    setQuitarArchivo(false);
    sexoManualRef.current = false;
    form.resetFields();
    form.setFieldsValue({
      fecha: dayjs(),
      estado: 'PENDIENTE_REVISION',
    });
    void loadLocalidadOptions();
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setArchivoFile(null);
    setQuitarArchivo(false);
    const localidadId =
      row.localidadId ||
      localidadOptions.find((opt) => opt.nombre === row.localidad)?.id ||
      undefined;
    const sexo = row.sexo || inferSexoFromNombre(row.nombreApellido) || undefined;
    sexoManualRef.current = false;
    form.setFieldsValue({
      fecha: parseFechaCalendario(row.fecha) ? dayjs(parseFechaCalendario(row.fecha)) : dayjs(),
      nombreApellido: row.nombreApellido,
      sexo,
      telefono: row.telefono || '',
      localidadId,
      email: row.email,
      especialidad: row.especialidad,
      observaciones: row.observaciones || '',
      estado: row.estado,
    });
    void loadLocalidadOptions();
    setModalOpen(true);
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      if (!editing && !archivoFile) {
        message.error('Debe adjuntar el archivo del currículum (PDF, Word o imagen)');
        return;
      }
      const fd = buildFormData(values);
      if (editing) {
        await api.put(`/curriculums/${editing.id}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Currículum actualizado');
      } else {
        await api.post('/curriculums', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('Currículum creado');
      }
      setModalOpen(false);
      setArchivoFile(null);
      setQuitarArchivo(false);
      load();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || 'Error al guardar');
    }
  };

  const cambiarEstado = async (row, estado) => {
    try {
      await api.patch(`/curriculums/${row.id}/estado`, { estado });
      message.success('Estado actualizado');
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al actualizar estado');
    }
  };

  const eliminar = async (row) => {
    try {
      await api.delete(`/curriculums/${row.id}`);
      message.success('Currículum eliminado');
      load();
    } catch (e) {
      message.error(e?.response?.data?.message || 'Error al eliminar');
    }
  };

  const descargarArchivo = async (row) => {
    try {
      const { data } = await api.get(`/curriculums/${row.id}/archivo`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = row.archivoNombre || `curriculum-${row.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'No se pudo descargar el archivo';
      try {
        const d = e?.response?.data;
        if (d instanceof Blob) {
          const txt = await d.text();
          const j = JSON.parse(txt);
          if (j?.message) msg = j.message;
        } else if (d?.message) {
          msg = d.message;
        }
      } catch {
        /* usar mensaje por defecto */
      }
      message.error(msg);
    }
  };

  const limpiarFiltros = () => {
    setFiltroNombre('');
    setFiltroLocalidad('');
    setFiltroEspecialidad('');
    setFiltroEstado(undefined);
    setFiltroSexo(undefined);
    setPage(1);
  };

  const columns = [
    {
      title: '',
      key: 'indicador',
      width: 40,
      align: 'center',
      fixed: isNarrow ? undefined : 'left',
      render: (_, row) => <EstadoDot estado={row.estado} size={12} />,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      width: 110,
      render: (v) => formatFechaCorta(v),
    },
    {
      title: 'Nombre y apellido',
      dataIndex: 'nombreApellido',
      ellipsis: true,
      minWidth: 160,
    },
    {
      title: 'Sexo',
      dataIndex: 'sexo',
      width: 56,
      align: 'center',
      render: (_, row) => sexoDisplay(row.sexo, row.nombreApellido),
    },
    {
      title: 'Localidad',
      key: 'localidad',
      ellipsis: true,
      width: 160,
      render: (_, row) =>
        row.provincia ? `${row.localidad} (${row.provincia})` : row.localidad || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      ellipsis: true,
      width: 160,
    },
    {
      title: 'Especialidad',
      dataIndex: 'especialidad',
      ellipsis: true,
      minWidth: 160,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: isNarrow ? 160 : 240,
      render: (estado, row) => (
        <Space size={8} align="center" style={{ width: '100%' }}>
          <EstadoDot estado={estado} size={10} />
          {hasPermission('curriculums', 'editar') ? (
            <Select
              size="small"
              value={estado}
              options={estadoOptions}
              optionRender={renderEstadoOption}
              labelRender={(opt) => renderEstadoOption(opt)}
              onChange={(v) => void cambiarEstado(row, v)}
              style={{ flex: 1, minWidth: 150 }}
            />
          ) : (
            <Tag color={ESTADO_COLORS[estado]} style={{ margin: 0 }}>
              {ESTADO_LABELS[estado] || estado}
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Archivo',
      key: 'archivo',
      width: 100,
      align: 'center',
      render: (_, row) =>
        row.tieneArchivo ? (
          <Button
            type="link"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => void descargarArchivo(row)}
          >
            {isNarrow ? '' : 'Descargar'}
          </Button>
        ) : (
          '—'
        ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 100,
      render: (_, row) => (
        <Space size={0}>
          {hasPermission('curriculums', 'editar') && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              title="Editar"
              onClick={() => openEdit(row)}
            />
          )}
          {hasPermission('curriculums', 'eliminar') && (
            <Popconfirm
              title="Eliminar currículum"
              description="Se eliminará el registro y el archivo adjunto."
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => void eliminar(row)}
            >
              <Button type="link" size="small" danger icon={<DeleteOutlined />} title="Eliminar" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Title level={5} style={{ margin: 0, color: '#198039' }}>
          Curriculums
        </Title>
      }
      style={{ borderColor: '#d8e8dc', background: '#ffffff' }}
      styles={{ body: { padding: isNarrow ? 12 : 16 } }}
      extra={
        hasPermission('curriculums', 'crear') ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuevo currículum
          </Button>
        ) : null
      }
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={24} sm={12} md={6}>
          <Input
            allowClear
            placeholder="Filtrar por nombre"
            value={filtroNombre}
            onChange={(e) => {
              setFiltroNombre(e.target.value);
              setPage(1);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            allowClear
            showSearch
            placeholder="Filtrar por localidad"
            value={filtroLocalidad || undefined}
            options={localidadOptions.map((opt) => ({
              value: opt.nombre,
              label: opt.label,
            }))}
            optionFilterProp="label"
            onChange={(v) => {
              setFiltroLocalidad(v || '');
              setPage(1);
            }}
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Input
            allowClear
            placeholder="Filtrar por especialidad"
            value={filtroEspecialidad}
            onChange={(e) => {
              setFiltroEspecialidad(e.target.value);
              setPage(1);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            allowClear
            placeholder="Filtrar por estado"
            style={{ width: '100%' }}
            value={filtroEstado}
            options={estadoOptions}
            onChange={(v) => {
              setFiltroEstado(v);
              setPage(1);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Select
            allowClear
            placeholder="Filtrar por sexo"
            style={{ width: '100%' }}
            value={filtroSexo}
            options={sexoOptions}
            onChange={(v) => {
              setFiltroSexo(v);
              setPage(1);
            }}
          />
        </Col>
        <Col xs={24}>
          <Space wrap size={[12, 6]} style={{ marginBottom: 4 }}>
            {estadoOptions.map((opt) => (
              <Space key={opt.value} size={6}>
                <EstadoDot estado={opt.value} size={8} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {opt.label}
                </Text>
              </Space>
            ))}
          </Space>
        </Col>
        <Col xs={24}>
          <Button size="small" onClick={limpiarFiltros}>
            Limpiar filtros
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1120 }}
        size="small"
        onRow={(record) => ({
          style: {
            borderLeft: `4px solid ${ESTADO_HEX[record.estado] || '#d9d9d9'}`,
          },
        })}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          showSizeChanger: false,
          onChange: (p) => setPage(p),
          showTotal: (t) => `${t} currículums`,
          size: 'small',
        }}
      />

      <Modal
        title={editing ? 'Editar currículum' : 'Nuevo currículum'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setArchivoFile(null);
          setQuitarArchivo(false);
        }}
        onOk={() => void submit()}
        okText="Guardar"
        width={isNarrow ? 'calc(100vw - 16px)' : 560}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          style={{ marginTop: 8 }}
          onValuesChange={(changed, all) => {
            if (Object.prototype.hasOwnProperty.call(changed, 'sexo')) {
              sexoManualRef.current = true;
            }
            if (
              Object.prototype.hasOwnProperty.call(changed, 'nombreApellido') &&
              !sexoManualRef.current
            ) {
              const inferredSexo = inferSexoFromNombre(all.nombreApellido);
              if (inferredSexo) form.setFieldsValue({ sexo: inferredSexo });
            }
          }}
        >
          <Form.Item
            name="fecha"
            label="Fecha"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="nombreApellido"
            label="Nombre y apellido"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="sexo"
            label="Sexo"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select placeholder="Seleccionar" options={sexoOptions} />
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono">
            <Input placeholder="Opcional" />
          </Form.Item>
          <Form.Item
            name="localidadId"
            label="Localidad"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select
              showSearch
              placeholder="Seleccionar localidad"
              options={localidadOptions.map((opt) => ({
                value: opt.id,
                label: opt.label,
              }))}
              optionFilterProp="label"
              notFoundContent="No hay localidades cargadas"
            />
          </Form.Item>
          <Form.Item
            name="email"
            label="Correo electrónico"
            rules={[
              { required: true, message: 'Requerido' },
              { type: 'email', message: 'Email inválido' },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="especialidad"
            label="Especialidad o formación específica"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="observaciones" label="Observaciones">
            <Input.TextArea rows={3} placeholder="Opcional" />
          </Form.Item>
          <Form.Item
            name="estado"
            label="Estado"
            rules={[{ required: true, message: 'Requerido' }]}
          >
            <Select options={estadoOptions} />
          </Form.Item>
          <Form.Item
            label={editing ? 'Archivo del currículum (PDF, Word o imagen)' : 'Archivo del currículum'}
            required={!editing}
          >
            {editing?.tieneArchivo && !archivoFile && !quitarArchivo && (
              <Space direction="vertical" size={4} style={{ display: 'flex', marginBottom: 8 }}>
                <Text type="secondary">
                  Archivo actual: {editing.archivoNombre}
                </Text>
                <Button
                  type="link"
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  style={{ padding: 0, width: 'fit-content' }}
                  onClick={() => setQuitarArchivo(true)}
                >
                  Quitar archivo
                </Button>
              </Space>
            )}
            {quitarArchivo && !archivoFile && (
              <Text type="warning" style={{ display: 'block', marginBottom: 8 }}>
                El archivo se eliminará al guardar. Podés subir otro antes de guardar.
              </Text>
            )}
            <Dragger
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/gif"
              maxCount={1}
              beforeUpload={(file) => {
                setArchivoFile(file);
                setQuitarArchivo(false);
                return false;
              }}
              onRemove={() => {
                setArchivoFile(null);
              }}
              fileList={
                archivoFile
                  ? [{ uid: '-1', name: archivoFile.name, status: 'done' }]
                  : []
              }
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Arrastrá o hacé clic para seleccionar</p>
              <p className="ant-upload-hint">
                PDF, Word (.doc, .docx) o imagen (.jpg, .png, .webp, .gif), máximo 15 MB
              </p>
            </Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Curriculums;
